import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { userAPI, shareholderAPI } from '../../api';
import { toast } from 'sonner';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  corporate_secretary: 'Corporate Secretary',
  secretariat_staff: 'Secretariat Staff',
  director: 'Director',
  committee_member: 'Committee Member',
  stockholder: 'Stockholder',
  management: 'Management',
  observer: 'Observer',
  external: 'External',
};

const UsersTab = ({ orgUsers, bodies, shareholders, fetchData }) => {
  const [usersSubTab, setUsersSubTab] = useState('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', password: '', role: 'observer' });
  const [saving, setSaving] = useState(false);

  // Shareholder state
  const [shDialogOpen, setShDialogOpen] = useState(false);
  const [editingSh, setEditingSh] = useState(null);
  const [shForm, setShForm] = useState({ name: '', shares: '', share_class: 'common', voting_rights: 1, email: '', address: '' });
  const [savingSh, setSavingSh] = useState(false);

  const secretariatUsers = orgUsers.filter(u => ['corporate_secretary', 'assistant_secretary', 'secretariat_staff', 'admin'].includes(u.role));
  const directorUsers = orgUsers.filter(u => ['director', 'committee_member'].includes(u.role));
  const committeeUsers = orgUsers.filter(u => u.role === 'committee_member');
  const resourcePersons = orgUsers.filter(u => ['observer', 'external'].includes(u.role));
  const otherUsers = orgUsers.filter(u => ['management', 'stockholder'].includes(u.role));

  const handleInviteUser = async () => {
    setSaving(true);
    try {
      await userAPI.invite(inviteForm);
      toast.success('User invited successfully');
      fetchData();
      setInviteDialogOpen(false);
      setInviteForm({ email: '', full_name: '', password: '', role: 'observer' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    } finally { setSaving(false); }
  };

  const handleSaveShareholder = async () => {
    if (!shForm.name || !shForm.shares) { toast.error('Name and shares required'); return; }
    setSavingSh(true);
    try {
      const data = { ...shForm, shares: parseInt(shForm.shares), voting_rights: parseFloat(shForm.voting_rights) };
      if (editingSh) {
        await shareholderAPI.update(editingSh.id, data);
        toast.success('Shareholder updated');
      } else {
        await shareholderAPI.create(data);
        toast.success('Shareholder added');
      }
      setShDialogOpen(false);
      setShForm({ name: '', shares: '', share_class: 'common', voting_rights: 1, email: '', address: '' });
      setEditingSh(null);
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSavingSh(false); }
  };

  const handleDeleteShareholder = async (id) => {
    if (!window.confirm('Delete this shareholder?')) return;
    try {
      await shareholderAPI.delete(id);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const openEditShareholder = (sh) => {
    setEditingSh(sh);
    setShForm({ name: sh.name || '', shares: sh.shares?.toString() || '', share_class: sh.share_class || 'common', voting_rights: sh.voting_rights || 1, email: sh.email || '', address: sh.address || '' });
    setShDialogOpen(true);
  };

  const getFilteredUsers = () => {
    if (usersSubTab === 'all') return orgUsers;
    if (usersSubTab === 'secretariat') return secretariatUsers;
    if (usersSubTab === 'board') return directorUsers;
    if (usersSubTab === 'committee') return committeeUsers;
    if (usersSubTab === 'resource') return resourcePersons;
    if (usersSubTab === 'others') return otherUsers;
    if (usersSubTab.startsWith('body_')) {
      const bodyId = usersSubTab.replace('body_', '');
      return orgUsers.filter(u => u.body_id === bodyId || u.committee_id === bodyId);
    }
    return orgUsers;
  };

  const subTabs = [
    { key: 'all', label: 'All Users', count: orgUsers.length },
    { key: 'shareholders', label: 'Shareholders', count: shareholders.length },
    { key: 'secretariat', label: 'Secretariat', count: secretariatUsers.length },
    { key: 'board', label: 'Board Members', count: directorUsers.length },
    { key: 'committee', label: 'Committee', count: committeeUsers.length },
    ...bodies.map(b => ({
      key: `body_${b.id}`,
      label: b.name,
      count: orgUsers.filter(u => u.body_id === b.id || u.committee_id === b.id).length,
    })),
    { key: 'resource', label: 'Resource Persons', count: resourcePersons.length },
    { key: 'others', label: 'Other Officers', count: otherUsers.length },
  ];

  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Users & Roles</h3>
          <p className="text-sm text-[#5A6570]">Manage team members, shareholders, and their permissions</p>
        </div>
        <div className="flex gap-2">
          {usersSubTab === 'shareholders' ? (
            <Button className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="add-shareholder-btn" onClick={() => { setEditingSh(null); setShForm({ name: '', shares: '', share_class: 'common', voting_rights: 1, email: '', address: '' }); setShDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Add Shareholder
            </Button>
          ) : (
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="invite-user-btn">
                  <Plus className="h-4 w-4 mr-2" />Invite User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite New User</DialogTitle>
                  <DialogDescription>Add a new team member to your organization</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2"><Label htmlFor="invite-name">Full Name</Label><Input id="invite-name" value={inviteForm.full_name} onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })} className="border-[#E2E6EA]" /></div>
                  <div className="space-y-2"><Label htmlFor="invite-email">Email</Label><Input id="invite-email" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} className="border-[#E2E6EA]" /></div>
                  <div className="space-y-2"><Label htmlFor="invite-password">Temporary Password</Label><Input id="invite-password" type="password" value={inviteForm.password} onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })} className="border-[#E2E6EA]" /></div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                      <SelectTrigger className="border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corporate_secretary">Corporate Secretary</SelectItem>
                        <SelectItem value="secretariat_staff">Secretariat Staff</SelectItem>
                        <SelectItem value="director">Director / Board Member</SelectItem>
                        <SelectItem value="committee_member">Committee Member</SelectItem>
                        <SelectItem value="stockholder">Stockholder</SelectItem>
                        <SelectItem value="management">Management / Officer</SelectItem>
                        <SelectItem value="observer">Resource Person (Regular)</SelectItem>
                        <SelectItem value="external">Resource Person (Specific/Ad-hoc)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleInviteUser} disabled={saving || !inviteForm.email || !inviteForm.full_name || !inviteForm.password} className="bg-[#00A3A3] hover:bg-[#008585]">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Invite User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Users Sub-Tabs */}
      <div className="flex gap-1.5 bg-[#F0F2F4] p-1 rounded-lg flex-wrap">
        {subTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setUsersSubTab(tab.key)}
            className={`text-xs px-3 py-1.5 rounded-md transition-colors font-medium ${
              usersSubTab === tab.key ? 'bg-white text-[#1A1D21] shadow-sm' : 'text-[#5A6570] hover:bg-white/50'
            }`}
            data-testid={`users-subtab-${tab.key}`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Shareholders Sub-Tab */}
      {usersSubTab === 'shareholders' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-[#E2E6EA]"><CardContent className="p-3"><p className="text-xs text-[#5A6570]">Total Shareholders</p><p className="text-lg font-bold">{shareholders.length}</p></CardContent></Card>
            <Card className="border-[#E2E6EA]"><CardContent className="p-3"><p className="text-xs text-[#5A6570]">Total Shares</p><p className="text-lg font-bold">{shareholders.reduce((s, sh) => s + (sh.shares || 0), 0).toLocaleString()}</p></CardContent></Card>
            <Card className="border-[#E2E6EA]"><CardContent className="p-3"><p className="text-xs text-[#5A6570]">Share Classes</p><p className="text-lg font-bold">{[...new Set(shareholders.map(s => s.share_class))].length}</p></CardContent></Card>
          </div>
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-[#E2E6EA] bg-[#F8F9FA]">
                    <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Name</th>
                    <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Shares</th>
                    <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Class</th>
                    <th className="text-left p-3 text-xs font-medium text-[#5A6570]">Email</th>
                    <th className="text-right p-3 text-xs font-medium text-[#5A6570]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shareholders.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-sm text-[#5A6570]">No shareholders added yet</td></tr>
                  ) : shareholders.map(sh => (
                    <tr key={sh.id} className="border-b border-[#E2E6EA] last:border-0">
                      <td className="p-3 font-medium text-sm text-[#1A1D21]">{sh.name}</td>
                      <td className="p-3 text-sm">{(sh.shares || 0).toLocaleString()}</td>
                      <td className="p-3"><span className="text-xs px-1.5 py-0.5 rounded bg-[#F0F2F4] text-[#5A6570] capitalize">{sh.share_class}</span></td>
                      <td className="p-3 text-sm text-[#5A6570]">{sh.email || '-'}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 mr-1" onClick={() => openEditShareholder(sh)}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteShareholder(sh.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>

          {/* Shareholder Dialog */}
          <Dialog open={shDialogOpen} onOpenChange={setShDialogOpen}>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingSh ? 'Edit' : 'Add'} Shareholder</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Name *</Label><Input value={shForm.name} onChange={(e) => setShForm(p => ({...p, name: e.target.value}))} className="border-[#E2E6EA]" data-testid="sh-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Shares *</Label><Input type="number" value={shForm.shares} onChange={(e) => setShForm(p => ({...p, shares: e.target.value}))} className="border-[#E2E6EA]" data-testid="sh-shares-input" /></div>
                  <div className="space-y-1.5"><Label>Share Class</Label>
                    <Select value={shForm.share_class} onValueChange={(v) => setShForm(p => ({...p, share_class: v}))}>
                      <SelectTrigger className="border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="common">Common</SelectItem><SelectItem value="preferred">Preferred</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={shForm.email} onChange={(e) => setShForm(p => ({...p, email: e.target.value}))} className="border-[#E2E6EA]" /></div>
                <div className="space-y-1.5"><Label>Address</Label><Input value={shForm.address} onChange={(e) => setShForm(p => ({...p, address: e.target.value}))} className="border-[#E2E6EA]" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveShareholder} disabled={savingSh} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-shareholder-btn">
                  {savingSh && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{editingSh ? 'Update' : 'Add'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        /* Users Table */
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-[#E2E6EA] bg-[#F8F9FA]">
                  <th className="text-left p-4 text-sm font-medium text-[#5A6570]">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-[#5A6570]">Email</th>
                  <th className="text-left p-4 text-sm font-medium text-[#5A6570]">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-[#5A6570]">Status</th>
                  <th className="text-right p-4 text-sm font-medium text-[#5A6570]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filtered = getFilteredUsers();
                  return filtered.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-sm text-[#5A6570]">No users in this category</td></tr>
                  ) : filtered.map((orgUser) => (
                    <tr key={orgUser.id} className="border-b border-[#E2E6EA] last:border-0">
                      <td className="p-4 font-medium text-[#1A1D21]">{orgUser.full_name}</td>
                      <td className="p-4 text-[#5A6570]">{orgUser.email}</td>
                      <td className="p-4">
                        <span className="text-xs px-2 py-1 rounded bg-[#00A3A3]/10 text-[#00A3A3]">
                          {ROLE_LABELS[orgUser.role] || orgUser.role}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded ${orgUser.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {orgUser.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default UsersTab;
